export interface IMediaProgressBar {
    id: string;
    maxValue: number;
    currentValue: number;
    downloaded: number;
    downloading: boolean;
    percentage?: number;
    estimatedTimeRemaining?: number; // in seconds
}

export class MediaProgressBar implements IMediaProgressBar {
    id = '';
    maxValue = 0;
    currentValue = 0;
    downloaded = 0; // MB downloaded
    downloading = false;
    percentage = 0;
    estimatedTimeRemaining = 0;
    
    // Timing properties (public for serialization/type checking)
    downloadStartTime = 0;
    lastUpdateTime = 0;
    lastDownloaded = 0;
    downloadSpeed = 0; // MB per second
    averageSpeeds: number[] = []; // For smoothing

    constructor() {
    }

    startDownload() {
        this.downloadStartTime = Date.now();
        this.lastUpdateTime = Date.now();
        this.lastDownloaded = this.downloaded;
        this.downloadSpeed = 0;
        this.averageSpeeds = [];
        this.estimatedTimeRemaining = 0;
    }

    updateProgress(currentDownloaded: number, totalSizeMB?: number) {
        const now = Date.now();
        
        // Initialize lastUpdateTime if not set
        if (this.lastUpdateTime === 0) {
            this.lastUpdateTime = now;
        }
        
        const timeElapsed = (now - this.lastUpdateTime) / 1000; // Convert to seconds
        
        // Update speed calculation only if significant time has passed (avoid division by zero and noise)
        if (timeElapsed > 0.5 && currentDownloaded > this.lastDownloaded) {
            const downloadedSinceLast = currentDownloaded - this.lastDownloaded;
            const instantSpeed = downloadedSinceLast / timeElapsed; // MB per second
            
            // Smooth the speed calculation using moving average
            this.averageSpeeds.push(instantSpeed);
            if (this.averageSpeeds.length > 5) {
                this.averageSpeeds.shift(); // Keep only last 5 measurements
            }
            
            this.downloadSpeed = this.averageSpeeds.reduce((sum, speed) => sum + speed, 0) / this.averageSpeeds.length;
            
            // Calculate estimated time remaining
            if (this.downloadSpeed > 0) {
                if (totalSizeMB && totalSizeMB > 0) {
                    // If we know total size, calculate based on remaining data
                    const remainingMB = totalSizeMB - currentDownloaded;
                    this.estimatedTimeRemaining = Math.max(0, remainingMB / this.downloadSpeed);
                } else if (this.maxValue > 0 && this.currentValue > 0) {
                    // If we know number of files, estimate based on average time per file
                    const averageTimePerFile = (now - this.downloadStartTime) / 1000 / this.currentValue;
                    const remainingFiles = this.maxValue - this.currentValue;
                    this.estimatedTimeRemaining = Math.max(0, averageTimePerFile * remainingFiles);
                }
            }
            
            // Update lastDownloaded only when speed is calculated
            this.lastDownloaded = currentDownloaded;
        }

        // Update file-based progress (always update percentage for visual feedback)
        if (this.maxValue > 0) {
            this.percentage = Math.min(100, Math.round((this.currentValue / this.maxValue) * 100));
        } else if (totalSizeMB && totalSizeMB > 0) {
            // For single file downloads, calculate percentage based on downloaded size
            this.percentage = Math.min(100, Math.round((currentDownloaded / totalSizeMB) * 100));
        }

        // Update downloaded amount
        this.downloaded = currentDownloaded;
        this.lastUpdateTime = now;
    }

    // Method to update when a file is completed
    completeFile() {
        this.currentValue += 1;
        this.updateProgress(this.downloaded);
    }

    // Method to get formatted time remaining
    getFormattedTimeRemaining(): string {
        if (!this.estimatedTimeRemaining || this.estimatedTimeRemaining <= 0 || !isFinite(this.estimatedTimeRemaining)) {
            return '--:--';
        }
        
        const totalSeconds = Math.round(this.estimatedTimeRemaining);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else {
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
    }

    // Method to get download speed as formatted string
    getFormattedSpeed(): string {
        if (this.downloadSpeed <= 0) {
            return '-- MB/s';
        }
        
        if (this.downloadSpeed < 1) {
            return `${(this.downloadSpeed * 1024).toFixed(1)} KB/s`;
        } else {
            return `${this.downloadSpeed.toFixed(1)} MB/s`;
        }
    }

    // Reset the progress bar
    reset() {
        this.currentValue = 0;
        this.maxValue = 0;
        this.downloaded = 0;
        this.downloading = false;
        this.percentage = 0;
        this.estimatedTimeRemaining = 0;
        this.downloadStartTime = 0;
        this.lastUpdateTime = 0;
        this.lastDownloaded = 0;
        this.downloadSpeed = 0;
        this.averageSpeeds = [];
    }

    // Check if download is stalled (no progress for 30 seconds)
    isStalled(): boolean {
        if (!this.downloading) return false;
        
        const timeSinceLastUpdate = (Date.now() - this.lastUpdateTime) / 1000;
        return timeSinceLastUpdate > 30 && this.downloadSpeed === 0;
    }
}